// The request outbox (CHT-750, contract section 7.6): the rows a state change of a request writes in ITS transaction, for a later
// consumer (a Stream notice, an Action Center item, a push). Delivery is never part of the transaction and nothing delivers in this
// run. A row holds IDS and CODES only: no note, no reason, no supplier name, no question or answer text. Both tables are
// deduplicated by a key built from the request, the history event and the request version, so a replay writes nothing twice.
export const notificationAudiences = Object.freeze(["CUSTOMER", "STAFF"]);
/** The history events that are a meaningful change for the person concerned, and the notification kind each one writes. */
const NOTIFICATION_KIND_OF_EVENT = {
    SUBMITTED: "REQUEST_SUBMITTED",
    ASSIGNED: "REQUEST_ASSIGNED",
    AWAITING_CUSTOMER: "REQUEST_AWAITING_CUSTOMER",
    CUSTOMER_RESPONDED: "REQUEST_CUSTOMER_RESPONDED",
    PAYMENT_REQUIRED: "REQUEST_PAYMENT_REQUIRED",
    CONFIRMED: "REQUEST_CONFIRMED",
    UNAVAILABLE: "REQUEST_UNAVAILABLE",
    COMPLETED: "REQUEST_COMPLETED",
    CANCELLED: "REQUEST_CANCELLED",
    DECLINED: "REQUEST_DECLINED",
    EXPIRED: "REQUEST_EXPIRED",
    // A supervisor handed the work to someone else: the new holder is told. The customer is not.
    OWNER_REASSIGNED: "REQUEST_ASSIGNED",
};
export const requestNotificationKinds = Object.freeze([
    "REQUEST_SUBMITTED",
    "REQUEST_ASSIGNED",
    "REQUEST_AWAITING_CUSTOMER",
    "REQUEST_CUSTOMER_RESPONDED",
    "REQUEST_PAYMENT_REQUIRED",
    "REQUEST_CONFIRMED",
    "REQUEST_UNAVAILABLE",
    "REQUEST_COMPLETED",
    "REQUEST_CANCELLED",
    "REQUEST_DECLINED",
    "REQUEST_EXPIRED",
]);
/** The staff kinds of `attention_events` for a request (the handoff kinds are in `handoff-attention.ts`). */
export const requestAttentionEventKinds = Object.freeze([
    "REQUEST_SUBMITTED",
    "REQUEST_CUSTOMER_RESPONDED",
    "REQUEST_UNASSIGNED",
    // Written by a later sweep that applies the optional aging threshold; nothing writes it in this run.
    "REQUEST_AGING",
]);
/** `request:<id>:<event>:<version>`: the unique key of the customer row. A staff row adds `:staff`. */
export const requestEventDedupeKey = (requestId, event, version, audience = "CUSTOMER") => `request:${requestId}:${event}:${version}${audience === "STAFF" ? ":staff" : ""}`;
/**
 * The outbox and attention rows of one change. Pure: the application writes them in the transaction of the change.
 *
 * - every meaningful event writes a `CUSTOMER` row (the customer-safe projection later reads the CURRENT request, not the row, so the
 *   events of one request converge whatever the order);
 * - a person who must act is told through a `STAFF` row: the new assignee or owner, the owner when the customer answered, and
 *   the whole queue (`staffUserId` null) when a request is submitted or lands in the supervisor queue;
 * - a request that lands with no named assignee (the supervisor queue, the coverage gap) also writes the attention event
 *   `REQUEST_UNASSIGNED`, a submission `REQUEST_SUBMITTED` and a customer answer `REQUEST_CUSTOMER_RESPONDED`.
 */
export function requestEventsForChange(change) {
    const kind = NOTIFICATION_KIND_OF_EVENT[change.event];
    if (kind === undefined)
        return { notifications: [], attention: [] };
    const payload = Object.freeze({
        v: 1,
        requestId: change.requestId,
        requestVersion: change.version,
        event: change.event,
        fromState: change.fromState,
        toState: change.toState,
        requestType: change.requestType,
    });
    const base = {
        customerId: change.customerId,
        subjectType: "concierge_request",
        subjectId: change.requestId,
        payload,
    };
    const notifications = [];
    const attention = [];
    const attend = (attentionKind, target, code) => attention.push({
        kind: attentionKind,
        requestId: change.requestId,
        customerId: change.customerId,
        requestVersion: change.version,
        targetStaffUserId: target,
        code,
    });
    const tell = (staffUserId) => notifications.push({
        ...base,
        kind,
        audience: "STAFF",
        staffUserId,
        dedupeKey: requestEventDedupeKey(change.requestId, change.event, change.version, "STAFF"),
    });
    // A reassignment is staff-internal: only the new holder hears of it.
    if (change.event !== "OWNER_REASSIGNED") {
        notifications.push({
            ...base,
            kind,
            audience: "CUSTOMER",
            staffUserId: null,
            dedupeKey: requestEventDedupeKey(change.requestId, change.event, change.version),
        });
    }
    switch (change.event) {
        case "SUBMITTED":
            tell(null);
            attend("REQUEST_SUBMITTED", null, change.requestType);
            break;
        case "ASSIGNED":
        case "OWNER_REASSIGNED": {
            // The designated assignee while ASSIGNED, the owner afterwards; nobody named: the whole queue.
            tell(change.toState === "ASSIGNED" ? change.assignedStaffUserId : change.ownerStaffUserId);
            if (change.event === "ASSIGNED" && change.assignedStaffUserId === null) {
                attend("REQUEST_UNASSIGNED", null, change.assignmentSource ?? "SUPERVISOR_QUEUE");
            }
            break;
        }
        case "CUSTOMER_RESPONDED":
            tell(change.ownerStaffUserId);
            attend("REQUEST_CUSTOMER_RESPONDED", change.ownerStaffUserId, "ANSWERED");
            break;
        default:
            break;
    }
    return { notifications, attention };
}
